/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-27 10:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, TextStyle, Link, Icon } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field } from 'redux-form';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import CustomDropZoneStandard from 'src/components/views/common/custom-dropzone-standard-v2';
import { URL } from 'src/constants/links';

import {
  LABEL_SHAPE_OPTIONS,
  SOLD_OUT_LABEL_DISPLAY_OPTIONS,
  SALE_LABEL_DISPLAY_VALUE,
  ACCEPT_FILE_IMAGE_TYPE,
  MAX_FILE_SIZE
} from 'src/constants/theme-settings';
import { LABEL_ALIGNMENT_OPTIONS } from 'src/constants/theme-settings.jsx';
import { DropdownIndicator, styles } from './react-select-style';
import { nonXSSv2, isLimitFileSize } from 'src/helpers/form-validation-helper';
import { checkHasXss, convertImageToBase64 } from 'src/helpers';

type Props = {
  formValues: any,
  prefix: string,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const nonXSSValue = nonXSSv2();
const limitSizeValidation = isLimitFileSize();

class SoldOutLabelForm extends Component<ComposedProps> {
  state = {
    error: ''
  };

  handleUnmountInput = () => {
    const { formValues, prefix, updateField } = this.props;
    const text = _.get(formValues, `${prefix}.text`, '');
    if (checkHasXss(text)) {
      updateField(`${prefix}.text`, '');
    }
  };

  handleUnmountDropZone = () => {
    if (this.state.error) {
      const { prefix, updateField } = this.props;
      updateField(`${prefix}.imgSrc`, '');
      updateField(`${prefix}.imgSize`, '');
      updateField(`${prefix}.imgName`, '');
      updateField(`${prefix}.imgSrc64`, '');
      this.setState({
        error: ''
      });
    }
  };

  onSelectSaleLabelImage = async files => {
    const { prefix, updateField } = this.props;
    let image = null;
    if (files[0].size <= MAX_FILE_SIZE) {
      image = await convertImageToBase64(files[0]);
    }

    updateField(`${prefix}.imgSrc`, files[0]);
    updateField(`${prefix}.imgSize`, files[0].size);
    updateField(`${prefix}.imgName`, files[0].name);
    updateField(`${prefix}.imgSrc64`, image);
  };

  render() {
    const { error } = this.state;
    const { i18n, prefix, formValues, labelTranslation, updateField } = this.props;

    const soldOutLabelDisplay = _.get(formValues, `${prefix}.displayType`, '');
    const soldOutLabelBackgroundColor = _.get(formValues, `${prefix}.backgroundColor`, '');
    const soldOutLabelColor = _.get(formValues, `${prefix}.color`, '');

    const soldOutLabelImg = _.get(formValues, `${prefix}.imgSrc`, null);
    const soldOutLabelImgSize = _.get(formValues, `${prefix}.imgSize`, 0);

    const formattedLabelShape = LABEL_SHAPE_OPTIONS.map(item => ({
      label: i18n.translate(item.label),
      value: item.value
    }));

    const formattedSoldOutLabelDisplay = SOLD_OUT_LABEL_DISPLAY_OPTIONS.map(item => ({
      label: i18n.translate(item.label),
      value: item.value
    }));

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['SoldOutLabelThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          {/* Hide other labels when sold out */}
          <div className="form-section__item">
            <Field
              name={`${prefix}.hideOtherLabelsWhenSoldOut`}
              component={RenderCheckbox}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_HideOtherLabel')}
            />
          </div>
        </div>

        <div className="form-section">
          {/* Label alignment */}
          <div className="form-section__item form-section__item--medium-spacing">
            <p className="title">
              {i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Arrangement_Item')}
            </p>
            <div className="radio-button-wrapper">
              {LABEL_ALIGNMENT_OPTIONS.map((item, index) => (
                <Field
                  key={index}
                  name={`${prefix}.alignment`}
                  id={item.value}
                  component={RenderRadioButton2}
                  label={item.icon}
                />
              ))}
            </div>
          </div>

          {/* Label shape */}
          <div className="form-section__item form-section__item--medium-spacing">
            <Field
              name={`${prefix}.shape`}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Shape_Item')}
              options={formattedLabelShape}
              component={RenderSelect2}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
            />
          </div>

          {/* Sold out label display */}
          <div className="form-section__item form-section__item--medium-spacing">
            <Field
              name={`${prefix}.displayType`}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Display_Item')}
              options={formattedSoldOutLabelDisplay}
              component={RenderSelect2}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
            />
          </div>

          {/* Sold out text */}
          {soldOutLabelDisplay === SALE_LABEL_DISPLAY_VALUE.TEXT && (
            <div className="form-section__item form-section__item--medium-spacing">
              <Field
                name={`${prefix}.text`}
                component={RenderTextField}
                label={i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Text_Item')}
                placeholder={
                  labelTranslation?.productItemSoldOutV2?.value ||
                  i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Text_Filled')
                }
                validate={[nonXSSValue]}
                onUnmount={this.handleUnmountInput}
              />
            </div>
          )}

          {/* Sold out image */}
          {soldOutLabelDisplay === SALE_LABEL_DISPLAY_VALUE.IMAGE && (
            <div className="form-section__item form-section__item--medium-spacing">
              <p className="title">
                {i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Image_Item')}
              </p>
              <Field
                name={`${prefix}.imgSrc`}
                component={CustomDropZoneStandard}
                allowMultiple={false}
                onSelectFile={this.onSelectSaleLabelImage}
                accept={ACCEPT_FILE_IMAGE_TYPE}
                fileTypes={ACCEPT_FILE_IMAGE_TYPE.split(', ')}
                defaultFile={soldOutLabelImg}
                defaultFileSize={soldOutLabelImgSize}
                validate={[limitSizeValidation]}
                onError={error => this.setState({ error: error })}
                onUnmount={this.handleUnmountDropZone}
              />

              {error && <TextStyle variation="negative">{error}</TextStyle>}
            </div>
          )}

          {soldOutLabelDisplay === SALE_LABEL_DISPLAY_VALUE.TEXT && (
            <div className="form-section__item form-section__item--medium-spacing">
              <p className="title title--small">
                {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
              </p>
              {/* Background color */}
              <RenderColorPickerItem
                colorValue={soldOutLabelBackgroundColor}
                fieldName={`${prefix}.backgroundColor`}
                fieldLabel={i18n.translate(
                  'AdminHTML.ThemeSetting.Form.soldOutLabel_BackgroundColor_Item'
                )}
                updateField={updateField}
              />

              {/* Color */}
              <RenderColorPickerItem
                colorValue={soldOutLabelColor}
                fieldName={`${prefix}.color`}
                fieldLabel={i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Color_Item')}
                updateField={updateField}
              />
            </div>
          )}
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(SoldOutLabelForm);
